import { useEffect } from "react";
import { useGetConfigBySlugName } from "src/views/assistant/AssistantServices";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import {
  useWorkflowProductDeclaration,
  useWorkflowResolvedPersona,
} from "../services/WorkflowProductService";
import type { WorkflowProductDescriptor } from "../types";

/**
 * Shape `useGetConfigBySlugName`'s camelCase result into the store's
 * PersonaData plus its operational config, mirroring useResolvePersona's
 * URL-persona branch. NOT PersonaConfigurationService.configurePersona: that
 * takes the raw snake_case API record, and this hook's data is already mapped —
 * feeding it there yields a config with no prompt, model or chat_config_id.
 */
const toPersonaData = (data: any) => ({
  id: data.chatConfigId,
  slug: data.slug || "",
  name: data.name || "",
  description: data.description || "",
  initialPrompt: data.initialPrompt || "",
  systemPrompt: data.systemPrompt || "",
  datasets: data.datasets || [],
  config: {
    modelId: data.modelId ?? null,
    modelVersionNum: data.modelVersionNum ?? null,
    chatConfigId: data.chatConfigId,
    initialPrompt: data.initialPrompt || "",
    systemPrompt: data.systemPrompt || "",
    datasets: data.datasets || [],
    topSearchResults: data.topSearchResults,
    toolCalling: data.toolCalling,
    modelCapabilities: data.modelCapabilities,
  },
  modelCapabilities: data.modelCapabilities,
});

/**
 * Fetch a persona by slug and select it into the ChatStore, ONLY while no
 * persona is set — a manual pick via ChatPersonaSelectButton is never
 * clobbered. The selection is real: create-dialogue and every turn carry the
 * persona's chat_config_id.
 */
const useApplyPersonaBySlug = (slug: string | undefined) => {
  const { data: personaData, isLoading } = useGetConfigBySlugName(slug);
  const { persona, setConfig, setPersona } = useChatStore();

  useEffect(() => {
    if (!personaData?.chatConfigId || persona) {
      return;
    }
    const resolved = toPersonaData(personaData);
    setPersona(resolved);
    setConfig(resolved.config);
  }, [personaData, persona, setConfig, setPersona]);

  return isLoading;
};

/**
 * Pre-select the product's resolved lead persona (declaration endpoint:
 * binding -> resource slot -> seeded default). Rebinding the slot changes
 * what a fresh splash pre-selects.
 */
export const useWorkflowLeadPersona = (
  product: WorkflowProductDescriptor,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { setPersona } = useChatStore();
  const { data: declaration, isLoading: isLoadingDeclaration } =
    useWorkflowProductDeclaration(enabled ? product.productKey : undefined);

  const leadSlot = declaration?.persona_slots.find(
    (slot) => slot.slot_key === product.leadSlotKey,
  );
  const leadSlug = enabled ? leadSlot?.resolved_persona?.slug : undefined;

  // The resolved persona always wins over whatever is already selected: a
  // selection carried in from another surface, or applied before an admin
  // rebound the slot, would drive the evaluation on the wrong persona and
  // model. The effect keys on the resolved slug alone, so it fires only when
  // the resolution changes — a manual pick on this surface never retriggers
  // it and sticks until an admin rebinds. The selection is read imperatively
  // at effect time (not a dependency) for the same reason; a selection
  // already matching the resolution is left alone to avoid a
  // clear-then-reapply flicker.
  useEffect(() => {
    if (!enabled || !leadSlug) {
      return;
    }
    if (useChatStore.getState().persona?.slug !== leadSlug) {
      setPersona(null);
    }
  }, [enabled, leadSlug, setPersona]);

  const isLoadingPersona = useApplyPersonaBySlug(leadSlug);

  return {
    declaration,
    leadSlot,
    isLoading: isLoadingDeclaration || (!!leadSlug && isLoadingPersona),
  };
};

/**
 * Pre-select the persona the backend resolver would run for a SPECIFIC
 * dialogue — for a PG stage dialogue its creation-time persona. This is what
 * makes the selector truthful on stage-mode splashes and across in-run
 * dialogue hops (pair with a persona reset on the hop).
 */
export const useWorkflowDialoguePersona = (
  product: WorkflowProductDescriptor,
  dialogueId: number | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const active = enabled && dialogueId != null;
  const { data, isLoading: isResolving } = useWorkflowResolvedPersona(
    active ? product.productKey : undefined,
    dialogueId,
  );
  const slug = active ? data?.persona?.slug : undefined;
  const isLoadingPersona = useApplyPersonaBySlug(slug);

  return { isLoading: isResolving || (!!slug && isLoadingPersona) };
};
